import { useCallback, useEffect, useRef, useState } from 'react'
import type { ModifySelectionRequest, SetSelectionRequest } from '../../types/case-container'
import { addToSelection, removeFromSelection, setSelection } from '../../services/selections'
import { logger } from '../../utils/logger'

const DEBOUNCE_MS = 500

export interface PendingChanges {
  add: Set<string>
  remove: Set<string>
  replace: string[] | null
}

function emptyChanges(): PendingChanges {
  return { add: new Set(), remove: new Set(), replace: null }
}

interface SyncReplaceArgs {
  caseId: string
  ids: string[]
}

async function syncReplace({ caseId, ids }: SyncReplaceArgs): Promise<void> {
  const request: SetSelectionRequest = { endpointIds: ids }
  await setSelection(caseId, request)
}

interface SyncIncrementalArgs {
  caseId: string
  toAdd: Set<string>
  toRemove: Set<string>
}

async function syncIncremental({ caseId, toAdd, toRemove }: SyncIncrementalArgs): Promise<void> {
  if (toAdd.size > 0) {
    const request: ModifySelectionRequest = { endpointIds: Array.from(toAdd) }
    await addToSelection(caseId, request)
  }
  if (toRemove.size > 0) {
    const request: ModifySelectionRequest = { endpointIds: Array.from(toRemove) }
    await removeFromSelection(caseId, request)
  }
}

export interface PendingSyncControl {
  pendingChanges: React.MutableRefObject<PendingChanges>
  syncing: boolean
  setSyncing: React.Dispatch<React.SetStateAction<boolean>>
  scheduleSync: () => void
  cancelPendingTimer: () => void
  clearPending: () => void
}

interface UsePendingSyncArgs {
  caseId: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
}

export function usePendingSync({ caseId, setError }: UsePendingSyncArgs): PendingSyncControl {
  const pendingChanges = useRef<PendingChanges>(emptyChanges())
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [syncing, setSyncing] = useState(false)

  const cancelPendingTimer = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }
  }, [])

  const clearPending = useCallback(() => {
    pendingChanges.current = emptyChanges()
  }, [])

  const flush = useCallback(async () => {
    if (!caseId) return
    const changes = pendingChanges.current
    pendingChanges.current = emptyChanges()
    const hasIncremental = changes.add.size > 0 || changes.remove.size > 0
    if (changes.replace === null && !hasIncremental) return
    setSyncing(true)
    try {
      await (changes.replace !== null
        ? syncReplace({ caseId, ids: changes.replace })
        : syncIncremental({ caseId, toAdd: changes.add, toRemove: changes.remove }))
      setError(null)
    } catch (err) {
      logger.warn('useSelection', 'Failed to sync selection to backend', err)
      setError(err instanceof Error ? err.message : 'Failed to sync selection')
    } finally {
      setSyncing(false)
    }
  }, [caseId, setError])

  const scheduleSync = useCallback(() => {
    cancelPendingTimer()
    debounceTimer.current = setTimeout(() => {
      void flush()
    }, DEBOUNCE_MS)
  }, [cancelPendingTimer, flush])

  useEffect(
    () => () => {
      cancelPendingTimer()
    },
    [cancelPendingTimer]
  )

  return { pendingChanges, syncing, setSyncing, scheduleSync, cancelPendingTimer, clearPending }
}
